import { useEffect, useState, useCallback } from "react";
import {
  fetchCategoryStats,
  mergeCategories,
  fetchContacts,
} from "../api/contacts";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import ContactDetailsModal from "../components/ContactDetailsModal";
import { FiSearch, FiMail, FiPhone, FiInfo } from "react-icons/fi";

export default function ContactCategories() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [newCategory, setNewCategory] = useState("");

  // Category contacts state
  const [activeCategory, setActiveCategory] = useState("");
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Modal state
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 12;

  const loadCategories = useCallback(async () => {
    if (!token?.access) return;
    try {
      setLoading(true);
      const data = await fetchCategoryStats(token.access);
      setCategories(data);
    } catch (err) {
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, [token?.access]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSelect = (cat) => {
    setSelected((prev) => {
      if (prev.includes(cat)) return prev.filter((c) => c !== cat);
      if (prev.length < 2) return [...prev, cat];
      toast.error("You can only select 2 categories");
      return prev;
    });
  };

  const handleMerge = async () => {
    if (selected.length !== 2) {
      toast.error("Please select exactly 2 categories");
      return;
    }
    if (!newCategory.trim()) {
      toast.error("Please enter a new category name");
      return;
    }

    try {
      await mergeCategories(token?.access, selected[0], selected[1], newCategory);
      toast.success("Categories merged successfully");
      setSelected([]);
      setNewCategory("");
      setMergeMode(false);
      loadCategories();
    } catch (err) {
      toast.error("Failed to merge categories");
    }
  };

  // Load contacts for active category
  useEffect(() => {
    const loadContacts = async () => {
      if (!activeCategory || !token?.access) return;
      setContactsLoading(true);
      try {
        const data = await fetchContacts(
          token.access,
          page,
          pageSize,
          "",
          activeCategory
        );
        setContacts(data.results);
        setTotalPages(data.total_pages);
      } catch (err) {
        console.error("Failed to fetch contacts:", err);
      } finally {
        setContactsLoading(false);
      }
    };
    loadContacts();
  }, [activeCategory, page, token?.access]);

  // Filtered categories based on search
  const filteredCategories = categories.filter((cat) =>
    cat.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Contact Categories ({categories.length})</h1>
        <button
          onClick={() => {
            setMergeMode((prev) => !prev);
            setSelected([]);
            setNewCategory("");
            setActiveCategory("");
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          {mergeMode ? "Cancel Merge" : "Merge Categories"}
        </button>
      </div>

      {/* Search */}
      <div className="mb-4 flex items-center gap-2">
        <FiSearch className="text-gray-400" />
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border rounded-lg px-3 py-2 w-full"
        />
      </div>

      {loading && <p>Loading...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && filteredCategories.length === 0 && (
        <p className="text-gray-600">No categories found.</p>
      )}

      {mergeMode && (
        <div className="mt-4 bg-gray-100 p-4 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold mb-3">Merge Selected Categories</h2>
          <p className="text-gray-600 mb-3">
            Selected: <span className="font-semibold text-blue-600">{selected.join(" + ") || "None"}</span>
          </p>

          <input
            type="text"
            placeholder="New category name"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="border rounded-lg px-3 py-2 w-full mb-3"
          />

          <button
            onClick={handleMerge}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            disabled={selected.length !== 2 || !newCategory.trim()}
          >
            Merge Now
          </button>
        </div>
      )}

      {/* Categories Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left px-4 py-2">Category</th>
              <th className="text-center px-4 py-2">Count</th>
              {mergeMode && <th className="text-center px-4 py-2">Select</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map((cat, idx) => (
              <tr
                key={idx}
                onClick={() =>
                  mergeMode
                    ? handleSelect(cat.category)
                    : setActiveCategory(cat.category)
                }
                className={`cursor-pointer hover:bg-gray-50 ${
                  mergeMode && selected.includes(cat.category) ? "bg-blue-100" : ""
                }`}
              >
                <td className="px-4 py-2">{cat.category}</td>
                <td className="text-center px-4 py-2">{cat.count}</td>
                {mergeMode && (
                  <td className="text-center px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selected.includes(cat.category)}
                      onChange={() => handleSelect(cat.category)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Contacts for active category */}
      {activeCategory && (
        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4">
            Contacts in <span className="text-blue-600">{activeCategory}</span>
          </h2>

          {contactsLoading && <p>Loading contacts...</p>}

          {!contactsLoading && contacts.length === 0 && (
            <p className="text-gray-500">No contacts in this category.</p>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="bg-white border rounded-xl shadow-sm p-5 flex flex-col justify-between hover:shadow-md transition"
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{contact.name}</h3>
                  <p className="text-sm text-gray-500">{contact.company_name}</p>
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <p className="flex items-center gap-2"><FiMail /> {contact.email}</p>
                    <p className="flex items-center gap-2"><FiPhone /> {contact.phone}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedId(contact.id);
                      setModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    <FiInfo size={16} /> View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <nav className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    onClick={() => setPage(num)}
                    className={`px-3 py-1 border rounded-lg ${
                      page === num ? "bg-blue-600 text-white" : "hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      )}

      <ContactDetailsModal
        contactId={selectedId}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
